<template>
  <div>
    <data-table 
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="tableEndpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../grid/DataTable.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface Props {
  orcamentoId: Id;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'edit-compromisso': [compromissoId: Id];
}>();

// Get i18n composable
const { t } = useI18n();

// Reactive data
const dataTableRef = ref<DataTableInstance | null>(null);

// Computed properties
const tableEndpoint = computed<string | undefined>(() => {
  if (!props.orcamentoId || props.orcamentoId === 0) {
    return undefined;
  }
  return `/index.php/compromissos/lista_compromissos_do_orcamento/${props.orcamentoId}`;
});

const tableColumns = computed(() => {
  const columns = [
    {
      data: 'compromisso_id',
      title: t('ID'),
      renderType: 'clickable_id'
    },
    {
      data: 'status',
      title: t('Status')
    },
    {
      data: 'operador',
      title: t('Operador')
    },
    {
      data: 'inicio',
      title: t('Início'),
      renderType: 'datetime'
    },
    {
      data: 'fim',
      title: t('Fim'),
      renderType: 'datetime'
    },
    {
      data: 'total',
      title: t('Total'),
      renderType: 'currency',
      footerType: 'sum',
      footerCurrency: true
    },
    {
      data: 'forma_de_pagamento',
      title: t('Forma de Pagamento')
    }
  ];

  return columns;
});

const tableConfig = computed(() => {
  return {
    tableId: 'tabelaCompromissosOrcamento',
    title: t('Compromissos do Orçamento'),
    filename: `compromissos_orcamento_${props.orcamentoId}_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    height: '400px',
    pagination: true,
    rowSelection: 'single'
  };
});

const filterConfig = computed(() => {
  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'compromisso_id': 'text',
      'status': 'text',
      'operador': 'text',
      'inicio': 'text',
      'fim': 'text',
      'total': 'text',
      'forma_de_pagamento': 'text'
    }
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

// Methods
const loadData = (): void => {
  log('CompromissosDoOrcamentoTable', 'loadData', 'Triggering grid refresh');
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('CompromissosDoOrcamentoTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  if (data.length > 0) {
    eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`compromissos do orçamento`));
  }
};

const handleDataError = (error: { message?: string } | Error | string): void => {
  log('CompromissosDoOrcamentoTable', 'handleDataError', 'Error loading data', error);
  const errMsg = error instanceof Error ? error.message : (typeof error === 'string' ? error : (error?.message || ''));
  eventBus.emit('globalMessage.error', t('Erro ao carregar compromissos do orçamento: ') + errMsg);
};

const editItem = (compromissoId: Id): void => {
  log('CompromissosDoOrcamentoTable', 'editItem', 'Editing ID', compromissoId);
  // Emit event to parent component to handle editing
  emit('edit-compromisso', compromissoId);
};

const handleRowClick = (rowData: { compromisso_id?: Id } | undefined): void => {
  log('CompromissosDoOrcamentoTable', 'handleRowClick', 'Row clicked', rowData?.compromisso_id || null);
  // Handle row click if needed
};

const handleEditClick = (compromissoId: Id | undefined): void => {
  log('CompromissosDoOrcamentoTable', 'handleEditClick', 'Edit clicked for compromisso ID', compromissoId);
  // The clickable_id renderType emits only the ID value (not full rowData)
  if (compromissoId) {
    editItem(compromissoId);
  }
};

// Watch for orcamentoId changes to reload data
watch(() => props.orcamentoId, (newId, oldId) => {
  if (newId && newId !== oldId && dataTableRef.value) {
    log('CompromissosDoOrcamentoTable', 'watch:orcamentoId', 'Orcamento ID changed from', oldId, 'to', newId);
    loadData();
  }
});

const handleCompromissoSaved = (): void => {
  log('CompromissosDoOrcamentoTable', 'event:compromisso.saved', 'Received');
  loadData();
};

const handleCompromissoDeleted = (): void => {
  log('CompromissosDoOrcamentoTable', 'event:compromisso.deleted', 'Received');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('CompromissosDoOrcamentoTable', 'mounted', 'Setting up event listeners');

  // Set up event listeners
  eventBus.on('compromisso.saved', handleCompromissoSaved);
  eventBus.on('compromisso.deleted', handleCompromissoDeleted);
});

onBeforeUnmount(() => {
  log('CompromissosDoOrcamentoTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('compromisso.saved', handleCompromissoSaved);
  eventBus.off('compromisso.deleted', handleCompromissoDeleted);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.text-center {
  text-align: center;
}
</style>
